import { Autocomplete, Button, Chip, IconButton, Popper, Stack, Switch, TextField, Typography } from "@mui/material";
import { Box, styled } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridFilterModel, GridSortModel, GridToolbarContainer } from "@mui/x-data-grid-pro";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useCallback, useRef } from "react";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { useAddScrapingMutation, useDisableKoBiddingManualMaxCpcMutation, useDisableScrapingMutation, useUpdateKoBiddingManualMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useAuth } from "src/auth";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import ModalScraping from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-scraping";
import { formatNumber } from "src/utils/format-number";
import { t } from "i18next";
import { formatCurrency } from "src/utils/format-currency";
import { FilterPanelCustom } from "../filter-panel-custom";
import ModalDeactivateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-deactivate-strategy";
import { useEnforceMaxCPCMutation } from "src/context/api/liveBidding/api";
import EnforceMaxCpcModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-enforce-max-cpc";
import ModalCreateStrategyOther from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy-other";

interface PercentageChangeProps {
    value: number | null;
    inverse?: boolean;
    rounded?: number;
    format?: string;
}

type FormatType = "percentage" | "pp";

const PercentageChange: React.FC<PercentageChangeProps & { format?: FormatType }> = ({
    value,
    inverse = false,
    rounded = 0,
    format = "percentage",
}) => {
    if (value === null || value === undefined) return null;

    const suffix = format === "pp" ? "pp" : "%";

    const isZeroAfterRound = Number(value.toFixed(rounded)) === 0;

    const isDash = value === 0 || isZeroAfterRound;

    const color =
        isDash
            ? "rgba(145, 158, 171, 1)"
            : value > 0
                ? (inverse ? "rgba(220, 24, 24, 1)" : "rgba(0, 162, 91, 1)")
                : value < 0
                    ? (inverse ? "rgba(0, 162, 91, 1)" : "rgba(220, 24, 24, 1)")
                    : "rgba(145, 158, 171, 1)";

    const style: React.CSSProperties = {
        color,
        marginLeft: "8px",
        textAlign: "right",
        fontVariantNumeric: "lining-nums tabular-nums",
        fontFamily: 'var(--font-family-Font-1, "Public Sans")',
        fontSize: "14px",
        lineHeight: "22px",
    };

    if (value > 1000) {
        return <span style={style}>{`>999${suffix}`}</span>;
    }

    if (value < -1000) {
        return <span style={style}>{`<-999${suffix}`}</span>;
    }

    const roundedValue = Number(value.toFixed(rounded));

    if (roundedValue === 0) {
        return <span style={style}>-</span>;
    }

    const sign = roundedValue > 0 ? "+" : "";

    return (
        <span style={style}>
            {sign}
            {roundedValue}
            {suffix}
        </span>
    );
};

interface Props {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    sortModel: GridSortModel;
    setSortModel: (model: GridSortModel) => void;
    filterModel: GridFilterModel;
    setFilterModel: (model: GridFilterModel) => void;
    localPatches: Record<string, any>;
    setLocalPatches: React.Dispatch<React.SetStateAction<Record<string, any>>>;


    selectedDateRange?: { label: string; value: string };
    onDateRangeChange?: (newRange: { label: string; value: string }) => void;

    selectedCompareDate?: any;
    onCompareDateChange?: (newRange: { label: string; value: string }) => void;

    selectedCountry: string;
    onOpenCsvImport: () => void;
    refetch: any
}

const GreenCheckSwitch = styled(Switch)(() => ({
    width: 36,
    height: 18,
    padding: 0,
    overflow: "hidden",

    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 0,
        top: 0,
        left: 0,
        transitionDuration: "200ms",

        "&.Mui-checked": {
            transform: "translateX(18px)",

            "& .MuiSwitch-thumb": {
                backgroundColor: "rgba(17, 141, 87, 1)",

                "&::before": {
                    content: '""',
                    width: 12,
                    height: 12,
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>\")",
                },
            },
        },
    },

    "& .MuiSwitch-thumb": {
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: "#C83737",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        "&::before": {
            content: '""',
            width: 12,
            height: 12,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>\")",
        },
    },

    "& .MuiSwitch-track": {
        width: 36,
        height: 18,
        borderRadius: 9,
        backgroundColor: "#e9ecee",
        opacity: 1,
    },

    "& .MuiSwitch-switchBase + .MuiSwitch-track": {
        backgroundColor: "#e9ecee",
    },

    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#e9ecee",
    },
}));

export default function DataGridKeywordOther({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    onDateRangeChange,
    onCompareDateChange,
    selectedCompareDate,
    selectedDateRange,
    selectedCountry,
    onOpenCsvImport,
    refetch
}: Props) {
    const [selectionRows, setSelectionRows]: any = useState([])
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const apiRef: any = useRef();
    const toolbarRef = useRef<HTMLDivElement>(null);

    const handleCloseScraping = useCallback(() => setModalScraping({ open: false, row: null }), []);

    const [addScraping] = useAddScrapingMutation();
    const [disableScraping] = useDisableScrapingMutation();
    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();

    const dateRangeOptions = [
        { label: 'Last 7 Days', value: 'last7d' },
        { label: 'Last 14 Days', value: 'last14d' },
        { label: 'Last 30 Days', value: 'last30d' },
    ];

    const comparisonPeriodOptions = [
        { label: 'Prev. Period', value: 'previous_period' },
        { label: 'Last Year', value: 'last_year' },
    ];

    const [modalScraping, setModalScraping] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalCreateStrategy, setModalCreateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalDeactivateStrategy, setModalDeactivateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [confirmDisable, setConfirmDisable] = useState<{ open: boolean; row: any | null, mode: string | null }>({
        open: false,
        row: null,
        mode: null
    });

    const [openEnforceModal, setOpenEnforceModal] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [loading, setLoading] = useState(false);
    const [scrapingDetails, setScrapingDetails] = useState<any[]>([]);

    const { user } = useAuth();
    const canImport = ["admin", "brand specialist"].includes(user?.role ?? "");

    const rowsToDisplay = useMemo(() => {
        const rowsArray = Array.isArray(modalScraping.row)
            ? modalScraping.row
            : modalScraping.row ? [modalScraping.row] : [];

        let result: any[] = [];
        for (const r of rowsArray) {
            const hasActive = r.scraping_details?.some((s: any) => s.status);
            if (hasActive) {
                result.push(...r.scraping_details.map((s: any, i: number) => ({
                    ...s,
                    id: s.id ?? `${r.id}-react-${i}`,
                    status: false,
                })));
            } else {
                result.push(...r.country_name.map((country: string, i: number) => ({
                    id: `${r.id}-new-${i}`,
                    keyword: r.keyword,
                    platform_scraping: ["Google"],
                    country,
                    frequency: "2 hours",
                    device: ["desktop", "mobile"],
                    status: true,
                    country_code: r.country_code,
                })));
            }
        }
        return result;
    }, [modalScraping]);

    const mergedRows = useMemo(() => {
        return data.map((row) => {
            const patch = localPatches[row.id];

            if (!patch) return row;

            return {
                ...row,
                ...patch,
                activations_details: patch.activations_details ?? row.activations_details,
            };
        });
    }, [data, localPatches]);

    const handleToggle = useCallback((row: any, currentStatus: boolean) => {
        if (currentStatus) {
            setConfirmDisable({ open: true, row, mode: "Scraping" });
        } else {
            setModalScraping({
                open: true,
                row: [row],
            });
        }
    }, []);

    const formatKeywordByMatchType = (keyword: string, matchType: string) => {
        if (!keyword) return "";

        switch (matchType) {
            case "PHRASE":
                return `"${keyword}"`;
            case "EXACT":
                return `[${keyword}]`;
            case "BROAD":
            default:
                return keyword;
        }
    };


    const ActivationCell = ({
        params,
        setModalActivationManual,
        setConfirmDisable,
        setModalCreateStrategy,
    }: any) => {

        const VALID_MODE = "KO Bidding Manual";

        const details = params.row.activations_details ?? [];

        const activeDetails = details.filter(
            (d: any) => d.status === true && d.mode?.startsWith(VALID_MODE)
        );

        const hasAnyMode = activeDetails.length > 0;

        const hasManualStrategy = activeDetails.some(
            (d: any) => d.mode?.startsWith("KO Bidding Manual")
        );

        const canCreateStrategy = !hasManualStrategy;

        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const [activeDetail, setActiveDetail] = useState<any>(null);

        const timeoutRef = useRef<any>(null);

        const handleEnter = (e: React.MouseEvent<HTMLElement>, detail: any) => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            setAnchorEl(e.currentTarget);
            setActiveDetail(detail);
        };

        const handleLeave = () => {
            timeoutRef.current = setTimeout(() => {
                setAnchorEl(null);
                setActiveDetail(null);
            }, 120);
        };

        const open = Boolean(anchorEl);

        const modeLabel = "Manual";

        return (
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    overflow: "hidden",
                    px: 1,
                }}
            >
                {hasAnyMode ? (
                    activeDetails.map((detail: any, i: number) => (
                        <Typography
                            key={i}
                            onMouseEnter={(e) => handleEnter(e, detail)}
                            onMouseLeave={handleLeave}
                            sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                py: 0.4,
                                px: 0.5,
                                height: 28,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                borderRadius: "6px",
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: "pointer",
                                color: "rgba(48, 57, 176, 1)",
                                background: "rgba(111, 120, 247, 0.19)",
                            }}
                        >
                            <Iconify icon="mingcute:pencil-3-line" width={16} />

                            <Box component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                                Manual
                            </Box>
                        </Typography>
                    ))
                ) : (
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
                        --
                    </Typography>
                )}

                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    placement="top"
                    style={{ zIndex: 2000 }}
                    modifiers={[
                        {
                            name: "offset",
                            options: { offset: [0, 8] },
                        },
                    ]}
                >
                    <Box
                        onMouseEnter={() => {
                            if (timeoutRef.current) clearTimeout(timeoutRef.current);
                        }}

                        onMouseLeave={handleLeave}
                        sx={{
                            display: "flex",
                            gap: 0.5,
                            p: 1,
                            background: "#fff",
                            borderRadius: "8px",
                            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.2,
                                py: 0.6,
                                borderRadius: "8px",
                                background: "rgba(111, 120, 247, 0.19)",
                                color: "rgba(48, 57, 176, 1)",
                            }}
                        >
                            <Iconify icon="mingcute:pencil-3-line" width={16} />

                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                {modeLabel}
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            onMouseDown={(e) => {
                                e.stopPropagation();

                                setConfirmDisable({
                                    open: true,
                                    row: params.row,
                                    mode: "KO Bidding Manual",
                                });

                                setAnchorEl(null);
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M5.25 1.75H8.75M1.75 3.5H12.25M11.0833 3.5L10.6742 9.63625C10.6129 10.5569 10.5822 11.0172 10.3833 11.3663C10.2083 11.6735 9.94422 11.9206 9.62597 12.0748C9.26448 12.25 8.80314 12.25 7.88045 12.25H6.11955C5.19686 12.25 4.73552 12.25 4.37403 12.0748C4.05577 11.9206 3.79172 11.6735 3.61666 11.3663C3.41781 11.0172 3.38713 10.5569 3.32575 9.63625L2.91667 3.5" stroke="#5E6D7B" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    </Box>
                </Popper>

                {canCreateStrategy && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            setModalCreateStrategy({
                                open: true,
                                row: [params.row],
                            });
                        }}
                        sx={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 22,
                            height: 22,
                            borderRadius: "6px",
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            opacity: 0,
                            transition: "all 0.2s ease",
                            ".MuiDataGrid-cell:hover &": {
                                opacity: 1,
                            },
                        }}
                    >
                        <Typography sx={{ fontSize: 20, lineHeight: 1 }}>
                            +
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "keyword",
            headerName: t("dashboard.global.keyword"),
            disableColumnMenu: true,
            flex: 1,
            minWidth: 285,
            headerClassName: "keyword-header",
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        fontWeight={600}
                        color="rgba(99, 115, 129, 1)"
                        fontSize={14}
                    >
                        {t("dashboard.global.keyword")}
                    </Typography>

                    <CustomTooltip title={"Ad clicks"}
                        placement="top"
                        arrow>
                        <Iconify icon="proicons:cursor-click" sx={{ cursor: 'help' }} width={20} height={20} />
                    </CustomTooltip>
                </Box>
            ),

            renderCell: (params) => {
                const formattedKeyword = formatKeywordByMatchType(
                    params.row.keyword,
                    params.row.match_type
                );

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            height: "60px",
                            padding: "0px 12px 0px 8px",
                            gap: 3,
                        }}
                    >
                        <CustomTooltip title={params.row.keyword} arrow>
                            <Typography
                                fontSize={14}
                                color="rgba(51, 60, 71, 1)"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: "help",
                                }}
                            >
                                {formattedKeyword}
                            </Typography>
                        </CustomTooltip>

                        <Typography
                            fontSize={14}
                            color="rgba(99, 115, 129, 1)"
                            sx={{
                                marginLeft: "auto",
                                textAlign: "right",
                            }}
                        >
                            {formatNumber(params.row.clicks)}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "keyword_id",
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Keyword ID
                    </Typography>
                </Box>
            ),
            headerName: "Keyword ID",
            minWidth: 200,
            type: "number",
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: "match_type",
            headerName: "Match Type",
            minWidth: 140,
            sortable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Match Type
                    </Typography>
                </Box>
            ),
            type: "singleSelect",
            valueOptions: [
                { value: "BROAD", label: "Broad" },
                { value: "PHRASE", label: "Phrase" },
            ],

            valueGetter: (params: any) => (params || "").toUpperCase(),

            renderCell: (params) => {
                const map: any = {
                    BROAD: "Broad",
                    PHRASE: "Phrase",
                };

                return map[params.value] || params.value;
            },
        },
        {
            field: "platform",
            headerName: "Platform",
            minWidth: 140,
            sortable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Platform
                    </Typography>
                </Box>
            ),
            type: "singleSelect",
            valueOptions: [
                { value: "GOOGLE", label: "Google Ads" },
                { value: "BING", label: "Bing Ads" },
            ],

            valueGetter: (params: any) => (params || "").toUpperCase(),

            renderCell: (params) => {
                const map: any = {
                    GOOGLE: "Google Ads",
                    BING: "Bing Ads",
                };

                return map[params.value] || params.value;
            },
        },
        // Campaigns
        {
            field: "campaign_names",
            sortable: true,
            disableColumnMenu: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign
                    </Typography>
                </Box>
            ),
            headerName: "Campaign",
            flex: 1,
            minWidth: 260,
            renderCell: ({ value }) => {
                return (
                    <CustomTooltip
                        title={value?.join(", ")}
                        placement="top"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    px: "12px",
                                    py: "8px",
                                    borderRadius: "8px",
                                    backgroundColor: "#FCFCFD",
                                    boxShadow: "1px 3px 8px 0 rgba(0, 0, 0, 0.08)",
                                },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                height: "60px",
                                px: "12px",
                                minWidth: 0,
                                overflow: "hidden",
                            }}
                        >
                            <Typography
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: '14px',
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    color: "rgba(51, 60, 71, 1)",
                                    cursor: 'help'
                                }}
                            >
                                {(value || []).join(", ")}
                            </Typography>
                        </Box>
                    </CustomTooltip>
                );
            }
        },
        {
            field: "campaign_ids",
            type: 'number',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Campaign ID
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Campaign ID",
            flex: 1,
            sortable: false
        },
        {
            field: "ad_group_names",
            disableColumnMenu: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Ad Group
                    </Typography>
                </Box>
            ),
            headerName: "Ad Group Name",
            minWidth: 200,
            sortable: true
        },
        {
            field: "ad_group_ids",
            type: 'number',
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Ad Group ID
                    </Typography>
                </Box>
            ),
            disableColumnMenu: true,
            headerName: "Ad Group ID",
            flex: 1,
            sortable: false
        },
        // Scraping Status
        {
            field: "scraping_status",
            disableColumnMenu: true,
            sortable: false,
            headerAlign: 'left',
            headerName: "Scraping Status",
            filterable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Scraping Status
                    </Typography>
                </Box>
            ),
            type: 'boolean',
            minWidth: 150,
            renderCell: ({ row }) => {
                const activeDetail = row.scraping_details?.find((s: any) => s.status === true);
                const freq = activeDetail?.frequency;

                const switchComponent = (
                    <Box
                        px="12px"
                        sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <GreenCheckSwitch
                            checked={!!activeDetail}
                            onChange={() => handleToggle(row, !!activeDetail)}
                        />
                    </Box>
                );

                if (activeDetail) {
                    return (
                        <CustomTooltip
                            title={freq ? `Frequency: ${freq}` : "No frequency"}
                            placement="top"
                            arrow
                        >
                            {switchComponent}
                        </CustomTooltip>
                    );
                }

                return switchComponent;
            },
        },
        {
            field: "mode",
            headerName: "Mode",
            disableColumnMenu: true,
            filterable: true,
            minWidth: 120,
            maxWidth: 120,
            width: 120,
            sortable: false,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                        flexDirection: "column"
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Mode
                    </Typography>
                </Box>
            ),
            editable: false,
            filterOperators: [
                {
                    label: "is any of",
                    value: "isAnyOf",
                    getApplyFilterFn: () => null,

                    InputComponent: (props) => {
                        const { item, applyValue } = props;

                        return (
                            <Autocomplete
                                sx={{ mt: 2.5 }}
                                multiple
                                options={[
                                    "KO Bidding Manual"
                                ]}
                                value={item.value || []}
                                onChange={(event, newValue) => {
                                    applyValue({
                                        ...item,
                                        value: newValue,
                                    });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        placeholder="Select modes"
                                    />
                                )}
                                size="small"
                            />
                        );
                    },
                },
            ],
            renderCell: (params) => (
                <ActivationCell
                    params={params}
                    setConfirmDisable={setConfirmDisable}
                    setModalCreateStrategy={setModalCreateStrategy}
                />
            )
        },
        {
            disableColumnMenu: true,
            field: "max_cpc",
            filterable: false,
            type: "string",
            sortable: false,
            headerAlign: 'left',
            headerName: "Max CPC",

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                    sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                    }}
                >
                    Set Max CPC
                </Typography>
            ),

            minWidth: 140,
            editable: true,
            renderEditCell: (params) => {
                return (
                    <Box
                        display="flex"
                        width="100%"
                        height="100%"
                        alignItems="center"
                        justifyContent="flex-end"
                        px={1}
                    >
                        <TextField
                            autoFocus
                            value={params.value ?? ""}
                            onChange={(event) => {
                                let value = event.target.value;

                                value = value.replace(",", ".");

                                params.api.setEditCellValue({
                                    id: params.id,
                                    field: params.field,
                                    value,
                                });
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{
                                style: { textAlign: "right" },
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    textAlign: "right",
                                    "& fieldset": {
                                        borderRadius: "6px",
                                    },
                                    "&:hover fieldset": {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        background: "rgba(255, 255, 255, 0.01)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                    "&.Mui-focused fieldset": {
                                        border: "1px solid var(--color-azure-58, #338AF3)",
                                        boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                    },
                                },
                                "& input": {
                                    textAlign: "right",
                                    cursor: "pointer",
                                },
                            }}
                        />
                    </Box>
                );
            },

            renderCell: (params: any) => {
                const activations = params.row.activations_details || [];

                const koManual = activations.find(
                    (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                );

                const koAuto = activations.find(
                    (d: any) => d.status === true && d.mode?.startsWith("KO Bidding Auto")
                );

                const value =
                    params.row.max_cpc !== undefined
                        ? params.row.max_cpc
                        : koManual?.max_cpc;

                const isAuto = !koManual && Boolean(koAuto);

                const isActive = Boolean(koManual);

                return (
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: 60,
                            alignItems: "center",
                            boxSizing: "border-box",
                        }}
                    >
                        {isActive ? (
                            isAuto ? (
                                <span style={{ opacity: 0.5, width: 52, textAlign: "right", display: "inline-block" }}>
                                    {params?.row?.max_cpc}
                                </span>
                            ) : (
                                <Box
                                    onClick={() => {
                                        params.api.startCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }}
                                    sx={{
                                        width: "100%",
                                        height: 36,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-end",
                                        px: "10px",
                                        borderRadius: "6px",
                                        border: "1px solid rgba(145, 158, 171, 0.20)",
                                        background: "rgba(255, 255, 255, 0.01)",
                                        fontSize: "0.875rem",
                                        lineHeight: 1,
                                        whiteSpace: "nowrap",
                                        boxSizing: "border-box",
                                        cursor: "pointer",
                                        transition: "all 0.15s ease",
                                        "&:hover": {
                                            border: "1px solid var(--color-azure-58, #338AF3)",
                                            boxShadow: "0 0 0 2px rgba(51, 138, 243, 0.35)",
                                        },
                                    }}
                                >
                                    {formatCurrency(value, workspace?.currency, lang, true, 2) ?? ""}
                                </Box>
                            )
                        ) : (
                            <Typography
                                sx={{
                                    width: '90%',
                                    textAlign: "right",
                                    fontFamily: '"Public Sans", sans-serif',
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    lineHeight: "14px",
                                    color: "#adb1b5",
                                }}
                            >
                                {formatCurrency(params.row.max_cpc_3, workspace?.currency, lang, true, 2) ?? ""}
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            field: "max_cpc_2",
            sortable: true,
            headerAlign: "left",
            disableColumnMenu: true,
            filterable: true,
            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    fontSize={14}
                    color="rgba(99, 115, 129, 1)"
                    sx={{
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                    }}
                >
                    Max CPC Evol
                </Typography>
            ),
            headerName: "Max CPC Evol",
            renderCell: ({ row }) => {
                const currentMaxCpc = row.max_cpc_2 ?? 0;
                const compareMaxCpc = row?.max_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareMaxCpc > 0) {
                    percentageChange =
                        ((currentMaxCpc - compareMaxCpc) / compareMaxCpc) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(currentMaxCpc, workspace?.currency, lang, true, 2) ?? ""}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },
            minWidth: 144,
            type: "number",
        },
        {
            field: "cost",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Ad Cost
                </Typography>
            ),

            headerName: "Ad Cost",

            renderCell: ({ row }) => {
                const currentCost = row.cost ?? 0;
                const compareCost = row?.cost_compare ?? 0;

                let percentageChange = null;

                if (compareCost > 0) {
                    percentageChange =
                        ((currentCost - compareCost) / compareCost) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "22px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentCost,
                                workspace?.currency,
                                lang,
                                true,
                                0
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },
            minWidth: 184,
            type: "number",
        },
        // AD CLICKS
        {
            field: "clicks",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: 'left',
            filterable: true,
            renderHeader: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography fontWeight={'600'} color={"rgba(99, 115, 129, 1)"} fontSize={14}>
                        Ad Clicks
                    </Typography>
                </Box>
            ),
            headerName: "Ad Clicks",
            renderCell: ({ row }) => {
                const currentClicks = row.clicks ?? 0;
                const compareClicks = row?.clicks_compare ?? 0;

                let percentageChange: number | null = null;

                if (compareClicks > 0) {
                    percentageChange = ((currentClicks - compareClicks) / compareClicks) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "22px",
                            boxSizing: "border-box",
                            gap: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatNumber(currentClicks)}
                        </Typography>
                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} />
                        </Box>
                    </Box>
                );
            },
            minWidth: 184,
            type: "number"
        },
        // AVG. CPC
        {
            field: "avg_cpc",
            disableColumnMenu: true,
            sortable: true,
            headerAlign: "left",
            filterable: true,

            renderHeader: () => (
                <Typography
                    fontWeight={600}
                    color="rgba(99, 115, 129, 1)"
                    fontSize={14}
                >
                    Avg. CPC
                </Typography>
            ),

            headerName: "Avg. CPC",

            renderCell: ({ row }) => {
                const currentCpc = row.avg_cpc ?? 0;
                const compareCpc = row?.avg_cpc_compare ?? 0;

                let percentageChange = null;

                if (compareCpc > 0) {
                    percentageChange =
                        ((currentCpc - compareCpc) / compareCpc) * 100;
                }

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: 60,
                            px: "12px",
                            boxSizing: "border-box",
                            gap: "8px",
                            width: "100%",
                        }}
                    >
                        <Typography
                            sx={{
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#333C47",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                            }}
                        >
                            {formatCurrency(
                                currentCpc,
                                workspace?.currency,
                                lang,
                                true,
                                2
                            )}
                        </Typography>

                        <Box
                            sx={{
                                width: 50,
                                textAlign: "right",
                                fontVariantNumeric: "lining-nums tabular-nums",
                                fontFamily: '"Public Sans", sans-serif',
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            <PercentageChange value={percentageChange} inverse />
                        </Box>
                    </Box>
                );
            },

            minWidth: 184,
            type: "number",
        },
    ], [handleToggle, workspace?.currency, lang, selectedCompareDate]);


    const handleConfirmScraping = useCallback(
        async (rowsToDelete: Set<any>) => {
            const rows = modalScraping.row;
            if (!rows || !workspace) return;

            setLoading(true);

            try {
                // 🔥 On récupère les détails à envoyer
                const details = scrapingDetails.length > 0 ? scrapingDetails : rowsToDisplay;

                // 🔥 On ne garde que les lignes NON supprimées
                const activeRows = details.filter((row) => !rowsToDelete.has(row.id));

                const payloadArray: any = activeRows.map((detail, index) => {
                    const originalRow = rows[index] ?? rows[0];

                    return {
                        keyword: detail.keyword ?? originalRow.keyword,
                        country: Array.isArray(detail.country) ? detail.country : [detail.country],
                        country_code: [originalRow.country_code[index]],
                        home_language: originalRow.home_language[0],
                        frequency: detail.frequency ?? "60 min",
                        platform_scraping: Array.isArray(detail.platform_scraping)
                            ? detail.platform_scraping
                            : [detail.platform_scraping ?? "Google"],
                        device: detail.device ?? ["desktop", "mobile"],
                        workspaceId: workspace.id,
                        client: workspace.bqTable,
                        mode: "Scraping",
                    };
                });

                const result = await addScraping(payloadArray).unwrap();

                enqueueSnackbar(`Scraping added successfully.`, { variant: "success" });

                // 🔥 Mise à jour locale
                rows.forEach((r: any) => {
                    setLocalPatches((prev) => ({
                        ...prev,
                        [r.id]: {
                            ...prev[r.id],
                            scraping_details: result.map((newScraping: any) => ({
                                ...newScraping,
                                status: true,
                            })),
                        },
                    }));
                });

                handleCloseScraping();
            } catch (err) {
                console.error(err);
                enqueueSnackbar(`Failed to add scraping`, { variant: "error" });
            } finally {
                setLoading(false);
            }
        },
        [
            modalScraping.row,
            workspace,
            scrapingDetails,
            rowsToDisplay,
            addScraping,
            setLocalPatches,
            handleCloseScraping
        ]
    );

    const handleDisableScraping = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableScraping({ id: detail.id }).unwrap();
                enqueueSnackbar(`Scraping disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        scraping_details: []
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Scraping`, { variant: "error" });
            }
        },
        [disableScraping, workspace, confirmDisable.row, setLocalPatches]
    );

    const handleConfirmDisable = useCallback(
        async (row: any) => {
            if (!row) return;

            const modeToDisable = confirmDisable.mode;

            try {

                if (modeToDisable === "Scraping") {
                    const detail = row.scraping_details?.[0];
                    await handleDisableScraping(detail);
                }

                else if (modeToDisable === "KO Bidding Manual") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "KO Bidding Manual");

                    if (detail?.id) {
                        await disableKoBiddingManualMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Manual disabled successfully`, { variant: "success" });

                        setLocalPatches(prev => {
                            const existing = prev[row.id] ?? row;

                            return {
                                ...prev,
                                [row.id]: {
                                    ...existing,
                                    activations_details: existing.activations_details.filter(
                                        (d: any) => d.mode !== "KO Bidding Manual"
                                    ),
                                    max_cpc: null,
                                }
                            };
                        });

                    }
                }

                refetch()
                setConfirmDisable({ open: false, row: null, mode: null });

            } catch (err) {
                enqueueSnackbar(`Failed to disable ${modeToDisable}`, { variant: "error" });
            }
        },
        [
            confirmDisable,
            handleDisableScraping,
            disableKoBiddingManualMaxCpc,
            setLocalPatches
        ]
    );

    const [enforceMaxCPC] = useEnforceMaxCPCMutation();
    const [updateMaxCpc] = useUpdateKoBiddingManualMutation();

    const handleUpdateMaxCPC = async (modifiedRows: any[]) => {
        try {
            const payload = modifiedRows.map((row) => ({
                id: row.id,
                workspaceId: workspace?.id,
                max_cpc: Number(row.max_cpc),
            }));

            await updateMaxCpc(payload).unwrap();

            setLocalPatches((prev) => {
                const next = { ...prev };

                for (const row of modifiedRows) {
                    next[row.rowId] = {
                        ...next[row.rowId],
                        max_cpc: row.max_cpc,
                        isEdited: false,
                        isInvalid: false,
                    };
                }

                return next;
            });

            setModifiedRows([]);

            const count = modifiedRows.length;

            enqueueSnackbar(
                `Update${count > 1 ? "s" : ""} applied successfully`,
                { variant: "success" }
            );

        } catch (error) {
            console.error("Update failed:", error);

            setLocalPatches({});
            setModifiedRows([]);
        }
    };

    const handleFinish = async (rows: any[], setResponse: (res: any[]) => void) => {

        const payload = {
            keywords: rows.map((row) => ({
                customer_id: String(row.account_id),
                account_id: String(row.account_id),
                ad_group_id: String(row.ad_group_ids[0]),
                platform: row?.platform === "GOOGLE" ? "Google Ads"
                    : row?.platform === "BING" ? "Bing Ads"
                        : row?.platform,

                keyword_id: String(row.keyword_id),
                keyword: row.keyword,
                match_type: row.match_type,
                campaign_id: String(row.campaign_ids[0]),
                max_cpc: row.max_cpc_to_enforce,
                name: workspace?.bqTable,
                country_code: row.country_code,
                message: 'Enforce max CPC from front-end'
            }))
        };


        try {
            const response = await enforceMaxCPC(payload).unwrap();
            setResponse(response);
        } catch (error) {
            console.error('Enforce error', error);
        }
    };

    const CustomToolbar = ({ selectionRows = [] }: any) => {
        return (
            <GridToolbarContainer
                ref={toolbarRef}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: 40,
                    minHeight: 40,
                    px: "20px",

                    "& .MuiButton-root": {
                        height: 32,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textTransform: "none",
                    },

                    "& .MuiAutocomplete-root": {
                        display: "flex",
                        alignItems: "center",
                    },

                    "& .MuiInputBase-root": {
                        height: 32,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        paddingTop: "0 !important",
                        paddingBottom: "0 !important",
                    },

                    "& .MuiAutocomplete-input": {
                        padding: "0 !important",
                    },

                    "& .MuiChip-root": {
                        height: 24,
                    },
                }}
            >
                {/* LEFT */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ height: "100%" }}
                >
                    <CustomTooltip title={"Show More KPIs"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showPreferences("columns")}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="mingcute:calendar-week-line"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Apply Filters"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showFilterPanel()}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="ic:round-filter-list"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>

                    <CustomTooltip title={"Download Table"} arrow placement="top">
                        <IconButton
                            onClick={() =>
                                apiRef.current?.exportDataAsCsv({
                                    fileName: "exact_activations",
                                    utf8WithBom: true,
                                })
                            }
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2" stroke="#919EAB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </IconButton>
                    </CustomTooltip>

                    {canImport && (
                        <CustomTooltip title={"Import KO Bidding Manual"} arrow placement="top">
                            <IconButton
                                onClick={onOpenCsvImport}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    padding: 0,
                                    color: '#919EAB',
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    }
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M14 8V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V8M5.33333 4.66667L8 2L10.6667 4.66667M8 2V10" stroke="#919EAB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </IconButton>
                        </CustomTooltip>
                    )}
                </Stack>

                {/* RIGHT */}
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ height: "100%" }}
                >
                    {selectionRows.length > 1 && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Button
                                onClick={() =>
                                    setOpenEnforceModal({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                sx={{
                                    backgroundColor:
                                        "rgba(145, 158, 171, 0.20)",
                                    borderRadius: "8px",
                                    px: 1.5,
                                }}
                                size="small"
                            >
                                Enforce Max CPC ({selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalDeactivateStrategy({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                sx={{
                                    backgroundColor:
                                        "rgba(145, 158, 171, 0.20)",
                                    borderRadius: "8px",
                                    px: 1.5,
                                }}
                                size="small"
                            >
                                Deactivate Bidding Strategy (
                                {selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalScraping({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                size="small"
                                variant="contained"
                            >
                                Active Scraping ({selectionRows.length})
                            </Button>

                            <Button
                                onClick={() =>
                                    setModalCreateStrategy({
                                        open: true,
                                        row: selectionRows,
                                    })
                                }
                                size="small"
                                variant="contained"
                                sx={{ mr: 1 }}
                            >
                                Activate Bidding Strategy (
                                {selectionRows.length})
                            </Button>
                        </Box>
                    )}

                    <Box
                        display="flex"
                        alignItems="center"
                        position="relative"
                    >
                        {modifiedRows.length > 0 && (
                            <Box display={"flex"} flexDirection={"row"} gap={1} px={1}>
                                <Button
                                    onClick={() => {
                                        setModifiedRows([]);
                                        setLocalPatches({});
                                    }}
                                    size="small"
                                    variant="contained"
                                >
                                    Discard ({modifiedRows.length})
                                </Button>

                                <Button
                                    onClick={() => handleUpdateMaxCPC(modifiedRows)}
                                    size="small"
                                    variant="contained"
                                    sx={{
                                        minWidth: "unset",
                                        padding: "2px 6px !important",
                                        fontSize: "12px !important",
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Apply ({modifiedRows.length})
                                </Button>
                            </Box>
                        )}

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            pr={"20px"}
                        >
                            <Autocomplete
                                disablePortal
                                options={dateRangeOptions}
                                size="small"
                                disableClearable
                                onChange={(event, newValue) => {
                                    onDateRangeChange?.(newValue);
                                }}
                                getOptionLabel={(option) => option.label}
                                sx={{ width: 150 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment:
                                                selectedDateRange ? (
                                                    <Chip
                                                        label={
                                                            selectedDateRange.label
                                                        }
                                                        size="small"
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "rgba(145, 158, 171, 0.50)",
                                                            },
                                                            backgroundColor:
                                                                "rgba(145, 158, 171, 0.32)",
                                                            color:
                                                                "rgba(51, 60, 71, 1)",
                                                        }}
                                                    />
                                                ) : null,
                                        }}
                                    />
                                )}
                            />

                            <Autocomplete
                                disablePortal
                                options={comparisonPeriodOptions}
                                disableClearable
                                size="small"
                                onChange={(event, newValue) => {
                                    onCompareDateChange?.(newValue);
                                }}
                                getOptionLabel={(option) => option.label}
                                sx={{ width: 160 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment:
                                                selectedCompareDate ? (
                                                    <Chip
                                                        label={
                                                            selectedCompareDate.label
                                                        }
                                                        size=
                                                        "small"
                                                        sx={{
                                                            "&:hover": {
                                                                backgroundColor: "rgba(145, 158, 171, 0.50)",
                                                            },
                                                            backgroundColor:
                                                                "rgba(145, 158, 171, 0.32)",
                                                            color:
                                                                "rgba(51, 60, 71, 1)",
                                                        }}
                                                    />
                                                ) : null,
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Box>
                </Box>
            </GridToolbarContainer>
        );
    };

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => {
        const saved = localStorage.getItem("columnVisibilityModelActivationsOther");
        return saved ? JSON.parse(saved) : {
            campaign_names: false,
            campaign_ids: false,
            ad_group_names: false,
            ad_group_ids: false,
            keyword_id: false,
            region_name: false,
            cost_last30d: false,
            platform: false,
            match_type: true,
        };
    });

    const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
        localStorage.setItem("columnVisibilityModelActivationsOther", JSON.stringify(newModel));
    }, []);


    const [modifiedRows, setModifiedRows] = useState<{ id: string | number; max_cpc: number }[]>([]);

    const parseNumber = (value: any) => {
        if (value === "" || value == null) return null;

        const normalized = String(value).replace(",", ".");

        const n = Number(normalized);

        return isFinite(n) ? n : null;
    };

    const handleRowUpdate = (newRow: any, oldRow: any) => {
        const raw = newRow.max_cpc;

        const nextValue = parseNumber(raw);
        const prevValue = parseNumber(oldRow.max_cpc);

        if (nextValue === prevValue) {
            return oldRow;
        }

        const koManual = newRow.activations_details?.find(
            (d: any) => d.mode === "KO Bidding Manual" && d.status === true
        );

        const isInvalid =
            koManual && (nextValue === null || nextValue < 0.01);

        const updatedRow = {
            ...newRow,
            isEdited: true,
            isInvalid,
        };

        if (koManual) {
            setModifiedRows((prev: any) => {
                const filtered = prev.filter((r: any) => r.rowId !== newRow.id);

                if (!isInvalid) {
                    return [
                        ...filtered,
                        {
                            rowId: newRow.id,
                            id: koManual.id,
                            max_cpc: nextValue,
                        }
                    ];
                }

                return filtered;
            });
        }

        return updatedRow;
    };

    return (
        <Box
            sx={{
                height: '715px',
                width: '100%',
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainer": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%!important",
                },
                "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainerContent": {
                    display: "flex",
                    flex: 1,
                },
                "& .MuiDataGrid-columnHeader": {
                    backgroundColor: 'rgba(250, 251, 251)',
                    borderLeft: "1px solid #e9ecee"
                },
                "& .keyword-header-wrapper": {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                },
                position: "relative",
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    borderLeft: "1px solid #e9ecee"
                },
                "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                },
                "& .MuiDataGrid-columnSeparator": {
                    color: "rgba(255,255,255,0.12)",
                    visibility: "visible",
                },

                "& .MuiDataGrid-cell:not(:last-of-type)": {
                    borderRight: "1px solid rgba(255,255,255,0.06)",
                },

                "& .css-ly985t-MuiDataGrid-root .MuiTablePagination-toolbar": {
                    color: '#919EAB!important'
                },
                "& .edited-row": {
                    backgroundColor: '#f1ebfb!important'
                },
                "& .MuiDataGrid-row.row-invalid": {
                    backgroundColor: "#ffe5e5 !important",
                },
                "& .MuiDataGrid-row:has(.Mui-error)": {
                    backgroundColor: "#ffe5e5 !important",
                },
                "& .MuiDataGrid-cell:has(.Mui-error) .MuiDataGrid-editInputCell input": {
                    backgroundColor: "#ffe5e5 !important",
                    border: '0px !important',
                },
                "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                    color: '#919EAB!important'
                },
                "& .css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input": {
                    borderRadius: '6px',
                    border: "1px solid rgba(145, 158, 171, 0.20)",
                    color: "#919EAB!important"
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root": {
                    color: "#919EAB!important",
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                    color: "#d0d5da!important",
                },
                "& .MuiDataGrid-cell:focus": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-cell:focus-within": {
                    outline: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none !important",
                },
                ".css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader, .css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-filler, .css-ly985t-MuiDataGrid-root .MuiDataGrid-row--borderBottom .MuiDataGrid-scrollbarFiller": {
                    backgroundColor: '#fafbfb!important'
                }
            }}
        >
            <DataGridPro
                rowHeight={57}
                processRowUpdate={handleRowUpdate}
                apiRef={apiRef}
                getRowId={(r) => r.id}
                rows={mergedRows}
                rowCount={rowCount}
                paginationModel={{ page, pageSize }}
                pageSizeOptions={[10, 25, 50, 100, 200]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                onPaginationModelChange={({ page, pageSize }) => {
                    onPageChange(page);
                    onPageSizeChange(pageSize);
                }}
                columnHeaderHeight={50}

                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                keepNonExistentRowsSelected
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                slots={{
                    toolbar: () => <CustomToolbar selectionRows={selectionRows} />,
                    filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                }}
                checkboxSelection
                onRowSelectionModelChange={(newSelection: any) => {
                    const selectedIds = Array.isArray(newSelection) ? newSelection : [];
                    setSelectionRows(
                        mergedRows.filter((row) => selectedIds.includes(row.id))
                    );
                }}
                getRowClassName={(params) =>
                    [
                        params.row.isEdited ? "edited-row" : "",
                        params.row.isInvalid ? "row-invalid" : ""
                    ].join(" ")
                }
                isCellEditable={(params) => {
                    if (params.field !== "max_cpc") return false;

                    const activations = params.row.activations_details || [];
                    const koManual = activations.find(
                        (d: any) => d.status === true && d.mode === "KO Bidding Manual"
                    );

                    return Boolean(koManual);
                }}
                disableRowSelectionOnClick
                columns={columns}
                sx={{
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e9ecee",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                    },
                }}
            />

            <ModalScraping
                open={modalScraping.open}
                loading={loading}
                rowsToDisplay={rowsToDisplay}
                onClose={handleCloseScraping}
                onConfirm={handleConfirmScraping}
                setModalScraping={setModalScraping}
                setScrapingDetails={setScrapingDetails}
            />

            <ModalCreateStrategyOther
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ModalDeactivateStrategy
                refetch={refetch}
                open={modalDeactivateStrategy.open}
                row={modalDeactivateStrategy.row}
                onClose={() => setModalDeactivateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />

            <EnforceMaxCpcModal
                onFinish={handleFinish}
                open={openEnforceModal.open}
                onClose={() => setOpenEnforceModal({ open: false, row: null })}
                selectedRows={openEnforceModal.row}
            />

        </Box >
    );
}